/**
 * Carte collection (Leaflet) — réinitialisable après filtre AJAX (Search & Discovery).
 * Données : #collection-map-locations-json + #collection-map-root[data-*].
 * Init paresseuse (IntersectionObserver) pour limiter tuiles + JS au scroll.
 */
(function () {
  'use strict';

  var mapLazyObserver = null;

  var lovenMapRuntime = {
    map: null,
    markerCluster: null,
    markersByProductId: null,
    defaultCenter: null,
    defaultZoom: null,
    userControlledViewport: false,
    /** Collections zone : filtre grille↔viewport uniquement après pan/zoom utilisateur. */
    mapViewportFilterByUser: false,
    mapViewportDebounceTimer: null,
    /** > 0 pendant invalidateSize (sheet mobile, layout) — ignorer moveend/zoomend programmatiques. */
    mapLayoutSyncDepth: 0,
  };

  /** Logs console : ajouter ?loven_map_debug=1 ou ?loven_debug=1 à l’URL */
  function isLovenMapConsoleDebug() {
    try {
      var s = window.location.search || '';
      return /\bloven_map_debug=1\b/.test(s) || /\bloven_debug=1\b/.test(s);
    } catch (e) {
      return false;
    }
  }

  var mapDebugSyncLogTimer = null;
  var selectedMarkerProductId = null;

  /** Rule 3 : pins sponsorisés +1000 au-dessus des pins organiques (même lat/lng déclusterisé). */
  var SPONSORED_PIN_ZINDEX_OFFSET = 1000;

  function applyMarkerZIndexOffset(marker) {
    if (!marker || typeof marker.setZIndexOffset !== 'function') return;
    marker.setZIndexOffset(
      isMarkerSponsored(marker) ? SPONSORED_PIN_ZINDEX_OFFSET : 0
    );
  }

  function createMapMarkerFromLoc(loc, isSelected) {
    var sponsored = isLocSponsored(loc);
    var marker = L.marker([loc.lat, loc.lon], {
      icon: createPriceIcon(loc.price || '', !!isSelected, sponsored),
      zIndexOffset: sponsored ? SPONSORED_PIN_ZINDEX_OFFSET : 0,
    });
    marker.__loc = loc;
    return marker;
  }

  /** Feuille liste repliée + preview fixe en bas (mobile uniquement). */
  function isMapPinPreviewMobileLayout() {
    try {
      return (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(max-width: 768px)').matches
      );
    } catch (e) {
      return false;
    }
  }

  function getMapPinPreviewHost() {
    var mapRoot = document.getElementById('collection-map-root');
    if (mapRoot && !isMapPinPreviewMobileLayout()) return mapRoot;
    return document.getElementById('CollectionAjaxResult');
  }

  function escapeMapHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function isLocSponsored(loc) {
    if (!loc) return false;
    var h = loc.handle ? String(loc.handle).toLowerCase().trim() : '';
    var mapHandles = window.LovenSponsoredMapHandles;
    var mapIds = window.LovenSponsoredMapIds;
    if (mapHandles instanceof Set && mapHandles.size) {
      if (h && mapHandles.has(h)) return true;
      if (loc.id != null && mapIds instanceof Set && mapIds.has(String(loc.id))) return true;
      return false;
    }
    if (
      h &&
      window.LovenSponsoredHandles instanceof Set &&
      window.LovenSponsoredHandles.has(h)
    ) {
      return true;
    }
    if (loc.id != null && window.LovenSponsored instanceof Set) {
      return window.LovenSponsored.has(String(loc.id));
    }
    return false;
  }

  /**
   * Plusieurs logements au même point GPS (ex. 2 variantes Escale Dinard) → décalage ~40 m
   * pour que chaque pin reste visible (surtout les sponsorisés, hors cluster).
   */
  function offsetColocatedMapLocations(locations) {
    if (!locations || !locations.length) return;
    var groups = {};
    var i;
    var loc;
    var key;
    var latN;
    var lonN;
    for (i = 0; i < locations.length; i++) {
      loc = locations[i];
      latN = Number(loc.lat);
      lonN = Number(loc.lon);
      if (!isFinite(latN) || !isFinite(lonN)) continue;
      key = latN.toFixed(5) + ',' + lonN.toFixed(5);
      if (!groups[key]) groups[key] = [];
      groups[key].push(loc);
    }
    Object.keys(groups).forEach(function (k) {
      var group = groups[k];
      if (group.length <= 1) return;
      var baseLat = Number(group[0].lat);
      var baseLon = Number(group[0].lon);
      var cosLat = Math.cos((baseLat * Math.PI) / 180) || 1;
      var stepM = 44;
      var j;
      var angle;
      for (j = 1; j < group.length; j++) {
        angle = (2 * Math.PI * j) / group.length;
        group[j].lat = baseLat + (stepM / 111320) * Math.cos(angle);
        group[j].lon = baseLon + (stepM / (111320 * cosLat)) * Math.sin(angle);
      }
    });
  }

  window.LovenOffsetColocatedMapLocations = offsetColocatedMapLocations;

  function createPriceIcon(price, isSelected, isSponsored) {
    var cls =
      'price__tag' +
      (isSelected ? ' price__tag--selected' : '') +
      (isSponsored ? ' price__tag--sponsored' : '');
    var inner =
      (isSponsored ? '<span class="price__tag-star" aria-hidden="true">★</span> ' : '') +
      escapeMapHtml(price);
    return L.divIcon({
      className: 'custom-icon' + (isSponsored ? ' custom-icon--sponsored' : ''),
      html: '<div class="' + cls + '">' + inner + '</div>',
      iconSize: null,
      iconAnchor: [0, 0],
    });
  }

  function isMarkerSponsored(marker) {
    return !!(marker && marker.__loc && isLocSponsored(marker.__loc));
  }

  function getMapLayerRefs() {
    var rt = lovenMapRuntime;
    return {
      map: rt.map || window.__lovenCollectionLeafletMap,
      cluster: rt.markerCluster || window.__lovenMarkerCluster,
    };
  }

  /** Retire un marqueur du cluster et de la carte (évite les doublons de couche). */
  function removeMarkerFromMapLayers(marker) {
    var layers = getMapLayerRefs();
    if (!marker || !layers.map) return;
    try {
      if (layers.cluster && layers.cluster.hasLayer(marker)) layers.cluster.removeLayer(marker);
    } catch (e) {}
    try {
      if (layers.map.hasLayer(marker)) layers.map.removeLayer(marker);
    } catch (e) {}
  }

  function isMarkerVisibleOnMap(marker) {
    var layers = getMapLayerRefs();
    if (!marker || !layers.map) return false;
    if (isMarkerSponsored(marker)) return layers.map.hasLayer(marker);
    if (layers.cluster) return layers.cluster.hasLayer(marker);
    return layers.map.hasLayer(marker);
  }

  /**
   * Sponsorisé → toujours sur la carte (jamais dans le cluster).
   * Autres → markerClusterGroup si disponible.
   */
  function addMarkerToMapLayers(marker) {
    var layers = getMapLayerRefs();
    if (!marker || !layers.map) return;
    removeMarkerFromMapLayers(marker);
    if (isMarkerSponsored(marker)) {
      marker.addTo(layers.map);
      applyMarkerZIndexOffset(marker);
    } else if (layers.cluster) {
      layers.cluster.addLayer(marker);
      applyMarkerZIndexOffset(marker);
    } else {
      marker.addTo(layers.map);
      applyMarkerZIndexOffset(marker);
    }
  }

  function applyMarkerSponsoredLayerPlacement(marker) {
    if (!marker) return;
    var onMap = isMarkerVisibleOnMap(marker);
    if (onMap) addMarkerToMapLayers(marker);
  }

  function repaintSponsoredMarkerIcons() {
    var rt = lovenMapRuntime;
    if (!rt || !rt.markersByProductId) return;
    Object.keys(rt.markersByProductId).forEach(function (pid) {
      var m = rt.markersByProductId[pid];
      if (!m || !m.__loc) return;
      var sponsored = isLocSponsored(m.__loc);
      m.setIcon(
        createPriceIcon(m.__loc.price || '', pid === selectedMarkerProductId, sponsored)
      );
      applyMarkerZIndexOffset(m);
      applyMarkerSponsoredLayerPlacement(m);
    });
  }

  function setSelectedMapMarker(productId) {
    selectedMarkerProductId = productId ? String(productId) : null;
    repaintSponsoredMarkerIcons();
  }

  /** Contenu aligné sur l’ancien popup desktop (titre + prix / nuit depuis loc). */
  function buildMapPinPreviewLocText(loc) {
    if (!loc) return '';
    var v = loc.ville || loc.ville_collection || loc.city || '';
    return String(v).trim();
  }

  function buildMapPinPreviewPriceHtml(loc) {
    var p = String(loc.price || '').trim();
    if (!p) return '';
    return (
      'À partir de <strong>' +
      escapeMapHtml(p) +
      '</strong> <span class="loven-grid-price-line__period">/ nuit</span>'
    );
  }

  function ensureMapPinPreviewEl() {
    var host = getMapPinPreviewHost();
    if (!host) return null;
    var el = document.getElementById('loven-map-pin-preview');
    if (el) {
      if (el.parentNode !== host) host.appendChild(el);
      return el;
    }
    el = document.createElement('div');
    el.id = 'loven-map-pin-preview';
    el.className = 'loven-map-pin-preview';
    el.setAttribute('hidden', '');
    el.innerHTML =
      '<div class="loven-map-pin-preview__media">' +
      '<img class="loven-map-pin-preview__img" alt="" loading="lazy" decoding="async">' +
      '<span class="loven-map-pin-preview__spons" hidden>' +
      '<span class="loven-map-pin-preview__spons-star" aria-hidden="true">★</span> En vedette</span>' +
      '<button type="button" class="loven-map-pin-preview__close" aria-label="Fermer l’aperçu">&times;</button>' +
      '</div>' +
      '<div class="loven-map-pin-preview__body">' +
      '<p class="loven-map-pin-preview__loc" hidden></p>' +
      '<h3 class="loven-map-pin-preview__title"></h3>' +
      '<p class="loven-map-pin-preview__price"></p>' +
      '<a class="loven-map-pin-preview__link">Voir le logement</a>' +
      '</div>';
    host.appendChild(el);
    el.querySelector('.loven-map-pin-preview__close').addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      closeMapPinPreview();
    });
    return el;
  }

  function resetMapPinPreviewPlacement(preview) {
    if (!preview) return;
    preview.classList.remove('loven-map-pin-preview--desktop');
    preview.style.top = '';
    preview.style.left = '';
    preview.style.bottom = '';
    preview.style.right = '';
    preview.style.maxHeight = '';
    preview.style.overflowY = '';
    preview.style.transform = '';
  }

  /**
   * Desktop : carte ancrée au pin (au-dessus si possible), clampée dans la carte — évite le crop en bas.
   */
  function positionDesktopMapPinPreview(preview, marker) {
    if (!preview || isMapPinPreviewMobileLayout()) return;
    var map = window.__lovenCollectionLeafletMap;
    var mapEl = document.getElementById('map');
    if (!map || !mapEl || !marker || !marker.getLatLng) return;

    preview.classList.add('loven-map-pin-preview--desktop');
    var pad = 12;
    var pinGap = 16;
    var pinLift = 28;
    var cardW = preview.offsetWidth || 320;
    var cardH = preview.offsetHeight || 360;
    var mapW = mapEl.offsetWidth;
    var mapH = mapEl.offsetHeight;
    var pt;

    try {
      pt = map.latLngToContainerPoint(marker.getLatLng());
    } catch (e) {
      return;
    }

    var left = pt.x - cardW / 2;
    var top = pt.y - cardH - pinGap - pinLift;

    if (top < pad) {
      top = pt.y + pinGap;
    }
    if (top + cardH > mapH - pad) {
      top = mapH - cardH - pad;
    }
    if (top < pad) {
      top = pad;
    }
    if (left < pad) {
      left = pad;
    }
    if (left + cardW > mapW - pad) {
      left = mapW - cardW - pad;
    }

    preview.style.position = 'absolute';
    preview.style.left = Math.round(left) + 'px';
    preview.style.top = Math.round(top) + 'px';
    preview.style.bottom = 'auto';
    preview.style.right = 'auto';
    preview.style.transform = '';
    preview.style.maxHeight = Math.max(200, mapH - pad * 2) + 'px';
    preview.style.overflowY = 'auto';

    var overflowBottom = top + cardH - (mapH - pad);
    if (overflowBottom > 0) {
      try {
        map.panBy([0, overflowBottom + pinGap], { animate: true, duration: 0.3 });
      } catch (e2) {
        map.panBy([0, overflowBottom + pinGap]);
      }
      window.setTimeout(function () {
        if (preview.hasAttribute('hidden')) return;
        try {
          pt = map.latLngToContainerPoint(marker.getLatLng());
          left = pt.x - cardW / 2;
          top = pt.y - cardH - pinGap - pinLift;
          if (top < pad) top = pt.y + pinGap;
          if (top + cardH > mapH - pad) top = mapH - cardH - pad;
          if (top < pad) top = pad;
          if (left < pad) left = pad;
          if (left + cardW > mapW - pad) left = mapW - cardW - pad;
          preview.style.left = Math.round(left) + 'px';
          preview.style.top = Math.round(top) + 'px';
        } catch (e3) {}
      }, 320);
    }
  }

  function scheduleDesktopMapPinPreviewLayout(preview, marker) {
    if (!preview || isMapPinPreviewMobileLayout()) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        positionDesktopMapPinPreview(preview, marker);
      });
    });
  }

  function closeMapPinPreview() {
    var el = document.getElementById('loven-map-pin-preview');
    if (el) {
      resetMapPinPreviewPlacement(el);
      el.setAttribute('hidden', '');
    }
    setSelectedMapMarker(null);
    if (isMapPinPreviewMobileLayout() && typeof window.LovenRoomsSheetRestoreAfterMapPreview === 'function') {
      window.LovenRoomsSheetRestoreAfterMapPreview();
    }
    var map = window.__lovenCollectionLeafletMap;
    if (map && map.closePopup) map.closePopup();
  }

  function openMapPinPreview(loc, marker) {
    if (!loc) return;
    var preview = ensureMapPinPreviewEl();
    if (!preview) return;
    var pid = String(loc.id || '');
    var title = loc.title || '';
    var href = loc.url || '#';
    var img = preview.querySelector('.loven-map-pin-preview__img');
    if (img) {
      if (loc.thumbnail) {
        img.src = loc.thumbnail;
        img.alt = title;
        img.hidden = false;
      } else {
        img.removeAttribute('src');
        img.alt = '';
        img.hidden = true;
      }
    }
    var sponsEl = preview.querySelector('.loven-map-pin-preview__spons');
    if (sponsEl) {
      if (isLocSponsored(loc)) sponsEl.removeAttribute('hidden');
      else sponsEl.setAttribute('hidden', '');
    }
    var locEl = preview.querySelector('.loven-map-pin-preview__loc');
    var locText = buildMapPinPreviewLocText(loc);
    if (locEl) {
      if (locText) {
        locEl.textContent = locText;
        locEl.removeAttribute('hidden');
      } else {
        locEl.textContent = '';
        locEl.setAttribute('hidden', '');
      }
    }
    var titleEl = preview.querySelector('.loven-map-pin-preview__title');
    if (titleEl) titleEl.textContent = title;
    var priceEl = preview.querySelector('.loven-map-pin-preview__price');
    if (priceEl) priceEl.innerHTML = buildMapPinPreviewPriceHtml(loc);
    var linkEl = preview.querySelector('.loven-map-pin-preview__link');
    if (linkEl) {
      linkEl.href = href;
      linkEl.setAttribute('aria-label', 'Voir ' + title);
    }
    preview.removeAttribute('hidden');
    setSelectedMapMarker(pid);
    if (isMapPinPreviewMobileLayout()) {
      resetMapPinPreviewPlacement(preview);
      if (typeof window.LovenRoomsSheetCollapseForMapPreview === 'function') {
        window.LovenRoomsSheetCollapseForMapPreview();
      }
    } else if (marker && marker.getLatLng) {
      var mapRef = window.__lovenCollectionLeafletMap;
      if (mapRef) {
        try {
          mapRef.panTo(marker.getLatLng(), { animate: true, duration: 0.35 });
        } catch (e) {
          mapRef.panTo(marker.getLatLng());
        }
      }
      scheduleDesktopMapPinPreviewLayout(preview, marker);
      if (img && loc.thumbnail) {
        img.addEventListener(
          'load',
          function onPreviewImgLoad() {
            img.removeEventListener('load', onPreviewImgLoad);
            positionDesktopMapPinPreview(preview, marker);
          },
          { once: true }
        );
      }
    }
  }

  function wireMapMarker(marker, loc) {
    if (!marker || !loc) return;
    marker.off('click');
    if (marker.unbindPopup) marker.unbindPopup();
    marker.on('click', function (e) {
      if (e && L && L.DomEvent) {
        L.DomEvent.stopPropagation(e);
        if (e.originalEvent) {
          e.originalEvent.preventDefault();
        }
      }
      document.dispatchEvent(new CustomEvent('lns:track', { detail: { event: 'lns_map_interact', action: 'pin', pin_item_id: loc.handle || null } }));
      openMapPinPreview(loc, marker);
    });
  }

  window.LovenMapPinPreviewClose = closeMapPinPreview;

  document.addEventListener('loven:sponsored-ready', function () {
    repaintSponsoredMarkerIcons();
    if (window.syncCollectionMapMarkersToProductGrid) {
      window.syncCollectionMapMarkersToProductGrid();
    }
  });

  /** Pour console.table : chemin + query tronquée (sans https://domaine/) */
  function shortBookingmoodCalendarLabel(raw) {
    var s = (raw || '').trim();
    if (!s) return '';
    try {
      var u = new URL(/^https?:\/\//i.test(s) ? s : 'https://' + s);
      var p = (u.pathname || '').replace(/^\/+/, '');
      var q = u.search || '';
      if (q.length > 48) q = q.slice(0, 45) + '…';
      return (p || u.hostname || '') + q;
    } catch (e) {
      return s.length > 72 ? s.slice(0, 69) + '…' : s;
    }
  }

  function normalizeEmbedKeyForLookup(raw) {
    if (typeof window.LovenNormalizeBookingmoodEmbedKey === 'function') {
      return window.LovenNormalizeBookingmoodEmbedKey(raw);
    }
    var s = (raw || '').trim();
    if (!s) return '';
    try {
      var u = new URL(/^https?:\/\//i.test(s) ? s : 'https://' + s);
      var h = (u.hostname || '').toLowerCase();
      var host =
        h === 'bookingmood.com' || /\.bookingmood\.com$/.test(h) ? 'bookingmood.com' : h.replace(/^www\./, '');
      var path = (u.pathname || '/').replace(/\/+$/, '');
      if (!path || path === '/') return host;
      return host + path;
    } catch (e) {
      return s.toLowerCase();
    }
  }

  function hasUrlDatesParams() {
    try {
      var p = new URLSearchParams(window.location.search);
      return !!(p.get('start') && p.get('end'));
    } catch (e) {
      return false;
    }
  }

  function getCollectionHandleFromPath() {
    var m = (window.location.pathname || '').match(/\/collections\/([^/?#]+)/i);
    return m ? String(m[1] || '').toLowerCase() : '';
  }

  /** /collections/<handle> hors search et catalogue global. */
  function isNamedCollectionCatalogPage() {
    if (typeof window.LovenIsNamedCollectionCatalogPage === 'function') {
      return window.LovenIsNamedCollectionCatalogPage();
    }
    if (!document.body || !document.body.classList.contains('template-collection')) return false;
    var handle = getCollectionHandleFromPath();
    return !!(handle && handle !== 'search' && handle !== 'tous-les-hebergements');
  }

  /** Collection zone (ex. /collections/bretagne) — pas search, pas grille API dynamique. */
  function isCollectionZoneMapPage() {
    if (!document.body || !document.body.classList.contains('template-collection')) return false;
    if (getCollectionHandleFromPath() === 'search') return false;
    if (window.__lovenCatalogMapFromApi) return false;
    if (isNamedCollectionCatalogPage()) return true;
    if (window.__lovenDynamicAvailabilityMode) return false;
    return true;
  }

  /** Layout carte + bottom sheet (#CollectionAjaxResult.loven-collection-map-mobile). */
  function isCollectionMapMobileLayout() {
    var ajaxRoot = document.getElementById('CollectionAjaxResult');
    try {
      return (
        ajaxRoot &&
        ajaxRoot.classList.contains('loven-collection-map-mobile') &&
        window.matchMedia('(max-width: 768px)').matches
      );
    } catch (e) {
      return false;
    }
  }

  /** Sync complète carte ↔ grille (filtres actifs, pas le browse collection par défaut). */
  function shouldSyncMapMarkersToVisibleGrid() {
    if (window.__lovenCatalogMapFromApi) return false;
    if (window.__lovenDynamicAvailabilityMode) return true;
    if (isCollectionZoneMapPage() && window.__lovenMapViewportFilterActive) return true;
    try {
      var p = new URLSearchParams(window.location.search);
      if (p.get('start') && p.get('end')) return true;
      if (p.get('dest_lat') && p.get('dest_lon')) return true;
      if ((window.location.search || '').indexOf('filter.') !== -1) return true;
    } catch (e) {}
    if (document.body && document.body.classList.contains('loven-dest-active')) return true;
    if (document.querySelector('#CollectionAjaxContent .grid-product.loven-geo-hidden')) {
      return true;
    }
    return false;
  }

  /** Page collection sans filtre : masquer seulement les pins sponsorisés sans fiche dans la grille. */
  function shouldHideOrphanSponsoredPins() {
    if (window.__lovenCatalogMapFromApi) return false;
    if (shouldSyncMapMarkersToVisibleGrid()) return false;
    return document.body && document.body.classList.contains('template-collection');
  }

  function beginMapLayoutSync() {
    lovenMapRuntime.mapLayoutSyncDepth = (lovenMapRuntime.mapLayoutSyncDepth || 0) + 1;
  }

  function endMapLayoutSync() {
    lovenMapRuntime.mapLayoutSyncDepth = Math.max(0, (lovenMapRuntime.mapLayoutSyncDepth || 1) - 1);
  }

  /** invalidateSize (sheet, chrome) sans déclencher filtre grille via moveend Leaflet. */
  function invalidateMapLayoutSize(map) {
    if (!map || typeof map.invalidateSize !== 'function') return;
    beginMapLayoutSync();
    try {
      map.invalidateSize({ animate: false });
    } catch (e) {}
    setTimeout(endMapLayoutSync, 500);
  }

  /** Filtre grille ↔ viewport carte (pan / zoom utilisateur, pas resize layout). */
  function shouldFilterGridByMapViewport() {
    if (!lovenMapRuntime.map) return false;
    if (window.__lovenCatalogMapFromApi) return false;
    var ajax = document.getElementById('CollectionAjaxContent');
    if (!ajax) return false;
    /* Mobile : la liste du sheet reste complète ; pas de masquage au resize carte. */
    if (isCollectionMapMobileLayout()) {
      return false;
    }
    /* Bretagne, Var, etc. : grille Shopify complète au load ; filtre après pan utilisateur. */
    if (isCollectionZoneMapPage() || isNamedCollectionCatalogPage()) {
      return !!lovenMapRuntime.mapViewportFilterByUser;
    }
    return !!ajax.querySelector('.grid-product[data-product-id]');
  }

  /** Réaffiche toute la grille statique (collections zone au chargement). */
  function clearMapViewportGridFilter() {
    document.querySelectorAll('.grid-product.loven-map-hidden').forEach(function (el) {
      el.classList.remove('loven-map-hidden');
      el.style.removeProperty('display');
    });
    window.__lovenMapViewportFilterActive = false;
    window.__lovenMapViewportVisible = undefined;
    lovenMapRuntime.lastViewportFilterBoundsKey = null;
    lovenMapRuntime.lastViewportFilterZoom = null;
  }

  function finalizeNamedCollectionCatalogGrid() {
    if (!isNamedCollectionCatalogPage()) return;
    lovenMapRuntime.mapViewportFilterByUser = false;
    clearMapViewportGridFilter();
    if (typeof window.LovenRunNamedCollectionCatalogBrowse === 'function') {
      window.LovenRunNamedCollectionCatalogBrowse();
    } else if (typeof window.LovenUpdateCollectionResultsCount === 'function') {
      window.LovenUpdateCollectionResultsCount();
    }
    document.dispatchEvent(new CustomEvent('collection:mapSync'));
  }

  function getGridProductsForViewportFilter(root) {
    if (!root) return [];
    if (isCollectionZoneMapPage()) {
      var staticWrap = root.querySelector('.loven-static-collection-grid');
      if (staticWrap) {
        return Array.prototype.slice.call(
          staticWrap.querySelectorAll('.grid-product[data-product-id]')
        );
      }
    }
    return Array.prototype.slice.call(root.querySelectorAll('.grid-product[data-product-id]'));
  }

  function getMapBoundsForViewportFilter(map) {
    if (!map || !map.getBounds) return null;
    var bounds = map.getBounds();
    if (!bounds || (typeof bounds.isValid === 'function' && !bounds.isValid())) return null;
    var padLat = Math.max(0.001, (bounds.getNorth() - bounds.getSouth()) * 0.015);
    var padLng = Math.max(0.001, (bounds.getEast() - bounds.getWest()) * 0.015);
    return L.latLngBounds(
      [bounds.getSouth() - padLat, bounds.getWest() - padLng],
      [bounds.getNorth() + padLat, bounds.getEast() + padLng]
    );
  }

  function filterProductGridByMapViewport(map) {
    if (!map || !shouldFilterGridByMapViewport()) return 0;
    var bounds = getMapBoundsForViewportFilter(map);
    if (!bounds) return 0;

    var root = document.getElementById('CollectionAjaxContent');
    if (!root) return 0;

    var cards =
      typeof window.LovenGetProductGridCards === 'function'
        ? window.LovenGetProductGridCards()
        : getGridProductsForViewportFilter(root);
    var visible = 0;

    cards.forEach(function (card) {
      if (card.classList.contains('loven-geo-hidden')) return;

      var plat = card.getAttribute('data-map-lat');
      var plon = card.getAttribute('data-map-lon');
      if (!plat || !plon) {
        card.classList.remove('loven-map-hidden');
        card.style.removeProperty('display');
        visible++;
        return;
      }

      var lat = parseFloat(plat, 10);
      var lng = parseFloat(plon, 10);
      if (isNaN(lat) || isNaN(lng)) {
        card.classList.remove('loven-map-hidden');
        card.style.removeProperty('display');
        visible++;
        return;
      }

      if (bounds.contains(L.latLng(lat, lng))) {
        card.classList.remove('loven-map-hidden');
        card.style.removeProperty('display');
        visible++;
      } else {
        card.classList.add('loven-map-hidden');
        card.style.display = 'none';
      }
    });

    window.__lovenMapViewportFilterActive = true;
    window.__lovenMapViewportVisible = visible;
    lovenMapRuntime.lastViewportFilterZoom = map.getZoom();
    lovenMapRuntime.lastViewportFilterBoundsKey =
      bounds.getSouth() +
      ',' +
      bounds.getWest() +
      ',' +
      bounds.getNorth() +
      ',' +
      bounds.getEast();
    return visible;
  }

  function applyViewportFilterPipeline(map, opts) {
    opts = opts || {};
    filterProductGridByMapViewport(map);

    if (typeof window.LovenMaintainGridOrderForMapViewport === 'function') {
      window.LovenMaintainGridOrderForMapViewport();
    }

    if (window.syncCollectionMapMarkersToProductGrid) {
      window.syncCollectionMapMarkersToProductGrid({ skipCamera: true });
    }

    if (typeof window.LovenUpdateCollectionResultsCount === 'function') {
      window.LovenUpdateCollectionResultsCount();
    }

    try {
      document.dispatchEvent(
        new CustomEvent('collection:mapViewportFiltered', {
          detail: { visible: window.__lovenMapViewportVisible },
        })
      );
    } catch (e) {}

    if (isLovenMapConsoleDebug() && !opts.silent) {
      try {
        console.info(
          '[Loven carte] Viewport → grille:',
          window.__lovenMapViewportVisible,
          'hébergement(s) visibles · zoom',
          map.getZoom(),
          '· rayon actif',
          window.__lovenMapActiveGeoRadiusKm || '—',
          'km'
        );
      } catch (e2) {}
    }
  }

  function runMapViewportGridPipeline(map, opts) {
    opts = opts || {};
    if (!map || !shouldFilterGridByMapViewport()) return;

    if (
      !opts.skipFetch &&
      typeof window.LovenFetchAvailabilityForMapViewport === 'function'
    ) {
      window.LovenFetchAvailabilityForMapViewport(map).then(function () {
        applyViewportFilterPipeline(map, opts);
      });
      return;
    }

    applyViewportFilterPipeline(map, opts);
  }

  function scheduleMapViewportGridUpdate(map, opts) {
    if (!map) return;
    clearTimeout(lovenMapRuntime.mapViewportDebounceTimer);
    lovenMapRuntime.mapViewportDebounceTimer = setTimeout(function () {
      function applyWhenMapSettled(tries) {
        tries = tries || 0;
        if (!map || !map.getBounds) return;

        if (map._animatingZoom && tries < 15) {
          setTimeout(function () {
            applyWhenMapSettled(tries + 1);
          }, 40);
          return;
        }

        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            runMapViewportGridPipeline(map, opts);
          });
        });
      }
      applyWhenMapSettled(0);
    }, 320);
  }

  function wireMapViewportGridSync(map) {
    if (!map || map.__lovenViewportGridWired) return;
    map.__lovenViewportGridWired = true;

    function onUserMapViewportChange(e) {
      if ((lovenMapRuntime.mapLayoutSyncDepth || 0) > 0) return;
      if (isCollectionMapMobileLayout()) return;
      /* setView / fitBounds / invalidateSize au chargement : pas de filtre grille. */
      if (!e || !e.originalEvent) return;
      lovenMapRuntime.userControlledViewport = true;
      // Tracking : premier zoom et premier déplacement de la page, pas plus (once).
      document.dispatchEvent(new CustomEvent('lns:track', { detail: { event: 'lns_map_interact', action: e.type === 'zoomend' ? 'zoom' : 'move', once: e.type } }));
      if (isCollectionZoneMapPage() || isNamedCollectionCatalogPage()) {
        lovenMapRuntime.mapViewportFilterByUser = true;
      }
      scheduleMapViewportGridUpdate(map);
    }

    map.on('moveend', onUserMapViewportChange);
    map.on('zoomend', onUserMapViewportChange);

    var mapEl = map.getContainer && map.getContainer();
    if (mapEl && typeof ResizeObserver !== 'undefined') {
      if (lovenMapRuntime.mapResizeObserver) {
        try {
          lovenMapRuntime.mapResizeObserver.disconnect();
        } catch (e) {}
      }
      lovenMapRuntime.mapResizeObserver = new ResizeObserver(function () {
        /* Bottom sheet mobile : hauteur carte change → invalidateSize seulement, pas filtre grille. */
        if (isCollectionMapMobileLayout()) {
          invalidateMapLayoutSize(map);
          clearMapViewportGridFilter();
          return;
        }
        if (isNamedCollectionCatalogPage() && !lovenMapRuntime.mapViewportFilterByUser) {
          invalidateMapLayoutSize(map);
          return;
        }
        if (!isCollectionZoneMapPage() || lovenMapRuntime.mapViewportFilterByUser) {
          scheduleMapViewportGridUpdate(map);
        }
      });
      lovenMapRuntime.mapResizeObserver.observe(mapEl);
    }
  }

  function getAvailabilityApiRowForEmbed(embedUrl) {
    var m = window.__lovenAvailabilityByEmbedKey;
    if (!embedUrl || !m || typeof m !== 'object') return null;
    var k = normalizeEmbedKeyForLookup(embedUrl);
    return k && m[k] ? m[k] : null;
  }

  /** Champs renvoyés par search-availability (hors URL longue) pour console.table */
  function formatAvailabilityApiPayloadForTable(row) {
    if (!row || typeof row !== 'object') return '';
    var parts = [];
    Object.keys(row).forEach(function (k) {
      var v = row[k];
      if (k === 'embed_url') {
        parts.push('embed:' + shortBookingmoodCalendarLabel(String(v)));
        return;
      }
      var s = typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v);
      if (s.length > 64) s = s.slice(0, 61) + '…';
      parts.push(k + ':' + s);
    });
    var out = parts.join(' | ');
    return out.length > 220 ? out.slice(0, 217) + '…' : out;
  }

  function getDispoApiLabel(embedUrl) {
    if (!hasUrlDatesParams()) return '—';
    if (window.__lovenAvailabilityByEmbedKey === undefined) return '…';
    if (!embedUrl) return '—';
    return getAvailabilityApiRowForEmbed(embedUrl) ? 'oui' : 'non';
  }

  function readJsonEl() {
    var el = document.getElementById('collection-map-locations-json');
    if (!el || !el.textContent) return null;
    try {
      var parsed = JSON.parse(el.textContent.trim());
      if (
        parsed &&
        Array.isArray(parsed.locations) &&
        parsed.locations.length > 0 &&
        !window.__lovenCatalogMapFromApi &&
        !window.__lovenCollectionMapLiquidBackup
      ) {
        window.__lovenCollectionMapLiquidBackup = JSON.parse(JSON.stringify(parsed));
      }
      return parsed;
    } catch (e) {
      return null;
    }
  }

  /** dest_lat / dest_lon / dest_radius_km (barre de recherche) — centre et niveau de zoom cohérents */
  function readDestSearchFromUrl() {
    try {
      var p = new URLSearchParams(window.location.search);
      var la = p.get('dest_lat');
      var lo = p.get('dest_lon');
      if (!la || !lo) return null;
      var plat = parseFloat(la, 10);
      var plng = parseFloat(lo, 10);
      if (isNaN(plat) || isNaN(plng)) return null;
      var fb =
        typeof window.__lovenReadDestRadiusFallbackKm === 'function'
          ? window.__lovenReadDestRadiusFallbackKm()
          : 50;
      var r = parseFloat(p.get('dest_radius_km') || String(fb), 10);
      if (isNaN(r) || r <= 0) r = fb;
      if (r === 80) r = 50;
      r = Math.min(500, Math.max(5, r));
      return { lat: plat, lng: plng, radiusKm: r };
    } catch (e) {
      return null;
    }
  }

  /** Même logique numérique que loven-search-bar (rayons thème : ville, région, etc.). */
  function numOrDestRadius(x, d) {
    var n = Number(x);
    return isNaN(n) || n <= 0 ? d : n;
  }

  /** Littéraux `search-bar.liquid` : window.__lovenDestRadius (cityKm, regionKm, …) */
  function getLovenDestRadiusForMap() {
    var w = window.__lovenDestRadius || {};
    return {
      city: numOrDestRadius(w.cityKm, 50),
      village: numOrDestRadius(w.villageKm, 40),
      department: numOrDestRadius(w.departmentKm, 90),
      region: numOrDestRadius(w.regionKm, 180),
      country: numOrDestRadius(w.countryKm, 500),
      defaultKm: numOrDestRadius(w.defaultKm, 50),
      nearMe: numOrDestRadius(w.nearMeKm, 50),
    };
  }

  function normalizeCollectionMapType(raw) {
    var t = String(raw || '')
      .toLowerCase()
      .trim();
    if (!t) return 'default';
    if (t.indexOf('ville') !== -1 || t === 'city') return 'city';
    if (t.indexOf('depart') !== -1 || t.indexOf('départ') !== -1) return 'department';
    if (t.indexOf('reg') !== -1 || t === 'region') return 'region';
    if (t.indexOf('equip') !== -1 || t.indexOf('équip') !== -1) return 'wide';
    return 'default';
  }

  function radiusKmFromCollectionMapType(type) {
    var p = getLovenDestRadiusForMap();
    if (type === 'city') return p.city;
    if (type === 'department') return p.department;
    if (type === 'region') return p.region;
    if (type === 'wide') return p.country;
    return p.defaultKm;
  }

  function readCollectionMapContext(root) {
    if (!root) return null;
    var type = normalizeCollectionMapType(root.dataset.collectionType || '');
    var radiusKm = radiusKmFromCollectionMapType(type);
    var zoom = parseInt(root.dataset.zoom, 10);
    if (isNaN(zoom)) zoom = zoomFromRadiusKm(radiusKm);
    var lat = parseFloat(root.dataset.centerLat);
    var lng = parseFloat(root.dataset.centerLng);
    if (isNaN(lat) || isNaN(lng)) return null;
    return { type: type, radiusKm: radiusKm, zoom: zoom, lat: lat, lng: lng };
  }

  function haversineKm(lat1, lon1, lat2, lon2) {
    var R = 6371;
    function toRad(d) {
      return (d * Math.PI) / 180;
    }
    var dLat = toRad(lat2 - lat1);
    var dLon = toRad(lon2 - lon1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function maxFitBoundsDiagonalM(collectionType, radiusKm) {
    var r = Number(radiusKm);
    if (!isNaN(r) && r > 0) return Math.min(500000, r * 2400);
    if (collectionType === 'city') return 120000;
    if (collectionType === 'department') return 220000;
    if (collectionType === 'region') return 360000;
    if (collectionType === 'wide') return 500000;
    return 180000;
  }

  function partitionLatLngsByRadiusKm(centerLat, centerLng, latLngs, radiusKm) {
    var inZone = [];
    var outZone = [];
    if (!latLngs || !latLngs.length) return { inZone: inZone, outZone: outZone };
    var i;
    var slack = radiusKm != null ? Number(radiusKm) : null;
    for (i = 0; i < latLngs.length; i++) {
      var ll = latLngs[i];
      if (!ll) continue;
      if (slack == null || isNaN(slack)) {
        inZone.push(ll);
      } else if (haversineKm(centerLat, centerLng, ll.lat, ll.lng) <= slack) {
        inZone.push(ll);
      } else {
        outZone.push(ll);
      }
    }
    return { inZone: inZone, outZone: outZone };
  }

  /**
   * Cadrage carte :
   * - fitBounds sur les pins dans la zone (hors outliers).
   * - Plancher de zoom = f(rayon recherche) : évite le dézoom régional (ex. Marseille + Toulon).
   * - Clusters serrés : fitBounds peut zoomer au-delà du plancher (maxZoom 14).
   */
  function percentileHaversineKm(centerLat, centerLng, latLngs, pct) {
    if (!latLngs || !latLngs.length) return null;
    var dists = [];
    var i;
    for (i = 0; i < latLngs.length; i++) {
      dists.push(haversineKm(centerLat, centerLng, latLngs[i].lat, latLngs[i].lng));
    }
    dists.sort(function (a, b) {
      return a - b;
    });
    var idx = Math.min(dists.length - 1, Math.floor(dists.length * pct));
    return dists[idx];
  }

  function cityViewportCapKm() {
    return numOrDestRadius(getLovenDestRadiusForMap().city, 50) + 15;
  }

  function resolveCityDestZoom(viewportCenter, viewportRadiusKm, inZoneLatLngs) {
    var capKm = cityViewportCapKm();
    var p75 = percentileHaversineKm(
      viewportCenter[0],
      viewportCenter[1],
      inZoneLatLngs,
      0.75
    );
    var frameKm = viewportRadiusKm != null ? viewportRadiusKm : capKm;
    if (p75 != null && p75 <= 40) {
      frameKm = Math.min(frameKm, Math.max(p75 * 2.5, 28));
    }
    frameKm = Math.min(frameKm, capKm);
    return zoomFromRadiusKm(frameKm);
  }

  function applyFitBoundsWithZoomFloor(map, latLngs, viewportCenter, targetZoom, opts) {
    opts = opts || {};
    if (!map || !latLngs || latLngs.length < 2) return false;
    map.fitBounds(L.latLngBounds(latLngs), {
      padding: opts.padding != null ? opts.padding : [48, 48],
      maxZoom: opts.maxZoom != null ? opts.maxZoom : 14,
      animate: false,
    });
    if (map.getZoom() < targetZoom) {
      map.setView(viewportCenter, targetZoom, { animate: false });
    }
    return true;
  }

  function collectionMapFitBoundsOptions(maxZoom) {
    var opts = {
      maxZoom: maxZoom != null ? maxZoom : 14,
      animate: false,
    };
    if (isCollectionMapMobileLayout()) {
      opts.paddingTopLeft = [28, 40];
      opts.paddingBottomRight = [28, 120];
    } else {
      opts.padding = [48, 48];
    }
    return opts;
  }

  function medianNumber(values) {
    if (!values || !values.length) return 0;
    var sorted = values.slice().sort(function (a, b) {
      return a - b;
    });
    var n = sorted.length;
    var mid = Math.floor(n / 2);
    if (n % 2) return sorted[mid];
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }

  /**
   * Pins hors cluster (coords erronées / outre-mer isolé).
   * Centre = médiane (pas barycentre) pour qu’un pin distant ne tire pas le cadre.
   * Un nuage national n’est pas recadré : on garde toute l’emprise.
   */
  function partitionCollectionOutlierLatLngs(latLngs) {
    var inZone = [];
    var outZone = [];
    if (!latLngs || !latLngs.length) return { inZone: inZone, outZone: outZone };
    var list = [];
    var i;
    for (i = 0; i < latLngs.length; i++) {
      if (latLngs[i]) list.push(latLngs[i]);
    }
    if (list.length <= 2) return { inZone: list, outZone: [] };

    var lats = [];
    var lngs = [];
    for (i = 0; i < list.length; i++) {
      lats.push(list[i].lat);
      lngs.push(list[i].lng);
    }
    var cLat = medianNumber(lats);
    var cLng = medianNumber(lngs);
    var scored = [];
    var dists = [];
    for (i = 0; i < list.length; i++) {
      var d = haversineKm(cLat, cLng, list[i].lat, list[i].lng);
      scored.push({ ll: list[i], d: d });
      dists.push(d);
    }
    var med = medianNumber(dists);
    var absDev = [];
    for (i = 0; i < dists.length; i++) {
      absDev.push(Math.abs(dists[i] - med));
    }
    var mad = medianNumber(absDev);
    var threshold = Math.max(med + 4.5 * Math.max(mad, med * 0.35), 40);
    for (i = 0; i < scored.length; i++) {
      if (scored[i].d > threshold && scored[i].d > 120) {
        outZone.push(scored[i].ll);
      } else {
        inZone.push(scored[i].ll);
      }
    }
    if (inZone.length < 2) {
      return { inZone: list, outZone: [] };
    }
    return { inZone: inZone, outZone: outZone };
  }

  function logCollectionAdaptiveCamera(map, visibleBounds, parts, boundsForFit, collCtx) {
    if (!isLovenMapConsoleDebug()) return;
    try {
      var fitBounds = L.latLngBounds(boundsForFit);
      var sw = fitBounds.getSouthWest();
      var ne = fitBounds.getNorthEast();
      var diagonalM = sw.distanceTo ? sw.distanceTo(ne) : 0;
      console.info('[Loven carte] Viewport', {
        mode: 'collection-adaptive',
        type: collCtx ? collCtx.type : 'default',
        zoomApplied: map.getZoom(),
        pinsTotal: visibleBounds.length,
        pinsInZone: parts.inZone.length,
        pinsOutliers: parts.outZone.length,
        diagonalKm: Math.round(diagonalM / 1000),
      });
    } catch (e) {}
  }

  /** Cadrage collection : fitBounds sur les pins affichés (hors outliers), pas le zoom Liquid de type. */
  function applyCollectionAdaptiveCamera(map, visibleBounds, defaultCenter, defaultZoom, collCtx) {
    var fallbackZoom = defaultZoom != null ? defaultZoom : 8;
    if (!visibleBounds || !visibleBounds.length) {
      if (defaultCenter) map.setView(defaultCenter, fallbackZoom, { animate: false });
      return;
    }
    if (visibleBounds.length === 1) {
      map.setView(visibleBounds[0], 13, { animate: false });
      logCollectionAdaptiveCamera(
        map,
        visibleBounds,
        { inZone: visibleBounds, outZone: [] },
        visibleBounds,
        collCtx
      );
      return;
    }

    var parts = partitionCollectionOutlierLatLngs(visibleBounds);
    var boundsForFit = parts.inZone.length >= 2 ? parts.inZone : visibleBounds;
    map.fitBounds(L.latLngBounds(boundsForFit), collectionMapFitBoundsOptions(14));
    if (map.getZoom() < 5) {
      map.setView(map.getCenter(), 5, { animate: false });
    }
    logCollectionAdaptiveCamera(map, visibleBounds, parts, boundsForFit, collCtx);
  }

  function applyMapCameraAfterSync(map, visibleBounds, dest, defaultCenter, defaultZoom, collCtx) {
    if (!map) return;

    if (dest) {
      var viewportRadiusKm = dest.radiusKm;
      var viewportCenter = [dest.lat, dest.lng];
      var targetZoom = zoomFromRadiusKm(dest.radiusKm);
      var slackKm = viewportRadiusKm != null ? viewportRadiusKm * 1.08 : null;
      var collectionType = collCtx ? collCtx.type : 'default';

      if (!visibleBounds.length) {
        map.setView(viewportCenter, targetZoom, { animate: false });
        return;
      }

      var centerLat = viewportCenter[0];
      var centerLng = viewportCenter[1];
      var parts = partitionLatLngsByRadiusKm(
        centerLat,
        centerLng,
        visibleBounds,
        slackKm
      );
      var boundsForFit = parts.inZone.length ? parts.inZone : visibleBounds;
      var hasOutliers = parts.outZone.length > 0;
      var cityCapKm = cityViewportCapKm();
      var p75 = percentileHaversineKm(centerLat, centerLng, boundsForFit, 0.75);
      var isCityScale =
        (viewportRadiusKm != null && viewportRadiusKm <= cityCapKm) ||
        (p75 != null && p75 <= 40);

      if (boundsForFit.length === 1) {
        map.setView(boundsForFit[0], Math.min(Math.max(targetZoom, 11), 14), {
          animate: false,
        });
      } else if (isCityScale) {
        map.setView(viewportCenter, resolveCityDestZoom(viewportCenter, viewportRadiusKm, boundsForFit), {
          animate: false,
        });
      } else if (boundsForFit.length >= 2) {
        applyFitBoundsWithZoomFloor(map, boundsForFit, viewportCenter, targetZoom, {
          maxZoom: hasOutliers ? Math.min(14, targetZoom + 1) : 14,
        });
      } else {
        map.setView(viewportCenter, targetZoom, { animate: false });
      }

      if (isLovenMapConsoleDebug()) {
        try {
          var fitBounds = L.latLngBounds(boundsForFit);
          var sw = fitBounds.getSouthWest();
          var ne = fitBounds.getNorthEast();
          var diagonalM = sw.distanceTo ? sw.distanceTo(ne) : 0;
          console.info('[Loven carte] Viewport', {
            mode: 'dest',
            type: collectionType,
            radiusKm: viewportRadiusKm,
            slackKm: slackKm,
            zoom: targetZoom,
            zoomApplied: map.getZoom(),
            cityScale: isCityScale,
            clusterP75Km: p75 != null ? Math.round(p75 * 10) / 10 : null,
            pinsTotal: visibleBounds.length,
            pinsInZone: parts.inZone.length,
            pinsOutliers: parts.outZone.length,
            fitBounds: boundsForFit.length >= 2 && !isCityScale,
            diagonalKm: Math.round(diagonalM / 1000),
          });
        } catch (e) {}
      }
      return;
    }

    applyCollectionAdaptiveCamera(map, visibleBounds, defaultCenter, defaultZoom, collCtx);
  }

  /**
   * Paires (rayon km, zoom) alignées sur les profils thème. Rayons issus de `settings` (Recherche disponibilité) ;
   * le zoom diminue quand le rayon augmente. Z valeurs cibles (Leaflet) : village serré → pays large.
   */
  function buildRadiusZoomAnchorsFromTheme() {
    var p = getLovenDestRadiusForMap();
    var raw = [
      { r: p.village, z: 12 },
      { r: p.city, z: 11 },
      { r: p.nearMe, z: 11 },
      { r: p.defaultKm, z: 10 },
      { r: p.department, z: 9 },
      { r: p.region, z: 8 },
      { r: p.country, z: 5 },
    ];
    var byR = {};
    for (var i = 0; i < raw.length; i++) {
      var it = raw[i];
      var rk = Math.round(it.r * 100) / 100;
      if (byR[rk] == null || byR[rk].z < it.z) {
        byR[rk] = { r: it.r, z: it.z };
      }
    }
    var out = Object.keys(byR).map(function (k) {
      return byR[k];
    });
    out.sort(function (a, b) {
      return a.r - b.r;
    });
    for (var m = 1; m < out.length; m++) {
      if (out[m].z > out[m - 1].z) {
        out[m].z = out[m - 1].z;
      }
    }
    return out;
  }

  function clampMapZoomLevel(z) {
    var n = Math.round(Number(z));
    if (isNaN(n)) return 10;
    return Math.min(18, Math.max(3, n));
  }

  function zoomInterpolateFromAnchors(anchors, r) {
    if (!anchors || !anchors.length) return 10;
    if (anchors.length === 1) {
      return clampMapZoomLevel(anchors[0].z);
    }
    if (r <= anchors[0].r) {
      return clampMapZoomLevel(Math.min(14, anchors[0].z + 1));
    }
    if (r >= anchors[anchors.length - 1].r) {
      return clampMapZoomLevel(anchors[anchors.length - 1].z);
    }
    for (var j = 0; j < anchors.length - 1; j++) {
      var a0 = anchors[j];
      var a1 = anchors[j + 1];
      if (r < a0.r) continue;
      if (r > a1.r) continue;
      if (a1.r - a0.r < 1e-6) {
        return clampMapZoomLevel(a0.z);
      }
      var t = (r - a0.r) / (a1.r - a0.r);
      return clampMapZoomLevel(a0.z + t * (a1.z - a0.z));
    }
    return clampMapZoomLevel(anchors[Math.floor(anchors.length / 2)].z);
  }

  /**
   * Zoom Leaflet = f(rayon en km), calé sur les mêmes rayons que la barre de destination (réglages thème).
   * Surcharge : window.__lovenMapZoom = function (km) { return 8; }
   */
  function zoomFromRadiusKm(radiusKm) {
    if (typeof window.__lovenMapZoom === 'function') {
      try {
        var z0 = Number(window.__lovenMapZoom(radiusKm));
        if (!isNaN(z0) && z0 >= 1 && z0 <= 18) return z0;
      } catch (e) {}
    }
    var r = Number(radiusKm);
    if (isNaN(r) || r <= 0) {
      r =
        typeof window.__lovenReadDestRadiusFallbackKm === 'function'
          ? window.__lovenReadDestRadiusFallbackKm()
          : 50;
    }
    r = Math.min(500, Math.max(5, r));
    var anchors = buildRadiusZoomAnchorsFromTheme();
    return zoomInterpolateFromAnchors(anchors, r);
  }

  function resetMapContainerEl(mapEl) {
    if (!mapEl) return;
    mapEl.innerHTML = '';
    if (mapEl._leaflet_id != null) {
      try {
        delete mapEl._leaflet_id;
      } catch (e) {
        mapEl._leaflet_id = undefined;
      }
    }
  }

  function destroyExistingMap() {
    clearTimeout(lovenMapRuntime.mapViewportDebounceTimer);
    lovenMapRuntime.mapViewportFilterByUser = false;
    lovenMapRuntime.userControlledViewport = false;
    if (lovenMapRuntime.mapResizeObserver) {
      try {
        lovenMapRuntime.mapResizeObserver.disconnect();
      } catch (e) {}
      lovenMapRuntime.mapResizeObserver = null;
    }
    lovenMapRuntime.map = null;
    lovenMapRuntime.markerCluster = null;
    lovenMapRuntime.markersByProductId = null;
    window.__lovenMarkerCluster = null;
    if (window.__lovenCollectionLeafletMap) {
      try {
        window.__lovenCollectionLeafletMap.remove();
      } catch (e) {}
      window.__lovenCollectionLeafletMap = null;
    }
    resetMapContainerEl(document.getElementById('map'));
  }

  function disconnectMapObserver() {
    if (mapLazyObserver) {
      mapLazyObserver.disconnect();
      mapLazyObserver = null;
    }
  }

  function schedulePostLayoutSync() {
    function sync() {
      if (window.syncCollectionMapMarkersToProductGrid) {
        window.syncCollectionMapMarkersToProductGrid({
          skipCamera: !!lovenMapRuntime.userControlledViewport,
        });
      }
      if (isNamedCollectionCatalogPage()) {
        finalizeNamedCollectionCatalogGrid();
      }
      window.dispatchEvent(new Event('resize'));
    }
    if (document.readyState === 'complete') {
      requestAnimationFrame(function () {
        requestAnimationFrame(sync);
      });
    } else {
      window.addEventListener(
        'load',
        function onLoad() {
          window.removeEventListener('load', onLoad);
          requestAnimationFrame(function () {
            requestAnimationFrame(sync);
          });
        },
        { once: true }
      );
    }
  }

  window.LovenCollectionMapInit = function () {
    var mapEl = document.getElementById('map');
    var root = document.getElementById('collection-map-root');
    if (!mapEl || typeof L === 'undefined') return;

    destroyExistingMap();

    var jsonlocations = readJsonEl();
    if (!jsonlocations || !jsonlocations.locations) return;

    var lat = root ? parseFloat(root.dataset.centerLat) : NaN;
    var lng = root ? parseFloat(root.dataset.centerLng) : NaN;
    var zoom = root ? parseInt(root.dataset.zoom, 10) : 8;
    var collCtx = readCollectionMapContext(root);
    var destSearch = readDestSearchFromUrl();
    if (destSearch) {
      lat = destSearch.lat;
      lng = destSearch.lng;
      zoom = zoomFromRadiusKm(destSearch.radiusKm);
    } else if (collCtx) {
      lat = collCtx.lat;
      lng = collCtx.lng;
      zoom = collCtx.zoom;
    }
    if (isNaN(lat) || isNaN(lng)) return;

    var map = L.map('map').setView([lat, lng], zoom);
    window.__lovenCollectionLeafletMap = map;
    map.on('click', function () {
      closeMapPinPreview();
    });

    lovenMapRuntime.markersByProductId = {};
    lovenMapRuntime.collectionContext = collCtx;
    var markersByProductId = lovenMapRuntime.markersByProductId;
    var defaultCenter = [lat, lng];
    var defaultZoom = zoom;
    lovenMapRuntime.defaultCenter = defaultCenter;
    lovenMapRuntime.defaultZoom = defaultZoom;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      updateWhenIdle: true,
      keepBuffer: 2,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    /** Regroupe les marqueurs (plugin leaflet.markercluster) pour limiter le travail du navigateur. */
    var markerCluster =
      typeof L.markerClusterGroup === 'function'
        ? L.markerClusterGroup({
            chunkedLoading: true,
            maxClusterRadius: 56,
            spiderfyOnMaxZoom: true,
            showCoverageOnHover: false,
            disableClusteringAtZoom: 16,
          })
        : null;
    if (markerCluster) {
      markerCluster.addTo(map);
      window.__lovenMarkerCluster = markerCluster;
    } else {
      window.__lovenMarkerCluster = null;
    }

    function isGridProductVisible(el) {
      if (!el || !document.documentElement.contains(el)) return false;
      if (el.hidden || el.getAttribute('aria-hidden') === 'true') return false;
      if (el.classList.contains('loven-geo-hidden')) return false;
      if (el.classList.contains('loven-map-hidden')) return false;
      if (el.style.display === 'none') return false;
      if (el.closest('[hidden]')) return false;
      var rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }

    function getVisibleProductIdsFromGrid() {
      var ids = {};
      var handles = {};
      document.querySelectorAll('.grid-product[data-product-id]').forEach(function (el) {
        var id = el.getAttribute('data-product-id');
        var handle = el.getAttribute('data-product-handle');
        if (!id && !handle) return;
        if (!isGridProductVisible(el)) return;
        if (id) ids[String(id)] = true;
        if (handle) handles[String(handle)] = true;
      });
      return { ids: ids, handles: handles };
    }

    function isMarkerVisibleInGrid(pid, loc, visibleIds, visibleHandles) {
      if (visibleIds && visibleIds[pid]) return true;
      var h = loc && loc.handle ? String(loc.handle) : '';
      if (h && visibleHandles && visibleHandles[h]) return true;
      return false;
    }

    function syncMapMarkersToProductGrid(syncOpts) {
      syncOpts = syncOpts || {};
      if (!map || !markersByProductId) return;

      var syncToGrid =
        shouldSyncMapMarkersToVisibleGrid() || !!window.__lovenMapViewportFilterActive;
      var hideOrphanSponsored = shouldHideOrphanSponsoredPins();
      var visibleGrid =
        syncToGrid || hideOrphanSponsored ? getVisibleProductIdsFromGrid() : null;
      var visibleIds = visibleGrid ? visibleGrid.ids : null;
      var visibleHandles = visibleGrid ? visibleGrid.handles : null;
      var hasProductCards = document.querySelector('.grid-product[data-product-id]') !== null;

      var visibleBounds = [];
      Object.keys(markersByProductId).forEach(function (pid) {
        var marker = markersByProductId[pid];
        if (!marker || !marker.__loc) return;
        var show;
        if (syncToGrid) {
          show = hasProductCards
            ? isMarkerVisibleInGrid(pid, marker.__loc, visibleIds, visibleHandles)
            : true;
        } else if (hideOrphanSponsored && hasProductCards && isMarkerSponsored(marker)) {
          show = isMarkerVisibleInGrid(pid, marker.__loc, visibleIds, visibleHandles);
        } else {
          show = true;
        }

        if (show) {
          if (!isMarkerVisibleOnMap(marker)) addMarkerToMapLayers(marker);
          visibleBounds.push(marker.getLatLng());
        } else {
          if (isMarkerVisibleOnMap(marker)) removeMarkerFromMapLayers(marker);
        }
      });

      var dest = readDestSearchFromUrl();
      var collCtxSync = lovenMapRuntime.collectionContext || readCollectionMapContext(document.getElementById('collection-map-root'));

      invalidateMapLayoutSize(map);

      if (!syncOpts.skipCamera && !lovenMapRuntime.userControlledViewport) {
        beginMapLayoutSync();
        applyMapCameraAfterSync(map, visibleBounds, dest, defaultCenter, defaultZoom, collCtxSync);
        setTimeout(endMapLayoutSync, 500);
      }

      if (isLovenMapConsoleDebug()) {
        try {
          var visGridDbg = getVisibleProductIdsFromGrid();
          var hasGridDbg = document.querySelector('.grid-product[data-product-id]') !== null;
          var rowsDbg = [];
          var nOn = 0;
          var nOff = 0;
          Object.keys(markersByProductId).forEach(function (pid) {
            var m = markersByProductId[pid];
            var loc = m && m.__loc;
            var onMap = hasGridDbg
              ? isMarkerVisibleInGrid(pid, loc, visGridDbg.ids, visGridDbg.handles)
              : true;
            if (onMap) nOn++;
            else nOff++;
            var embedU = loc && loc.embedUrl ? loc.embedUrl : '';
            var apiRow = getAvailabilityApiRowForEmbed(embedU);
            rowsDbg.push({
              productId: pid,
              title: loc ? loc.title : '',
              lat: loc && loc.lat != null ? loc.lat : '',
              lon: loc && loc.lon != null ? loc.lon : '',
              calendrier: loc && loc.embedUrl ? shortBookingmoodCalendarLabel(loc.embedUrl) : '',
              dispoApi: getDispoApiLabel(embedU),
              detailApi: apiRow ? formatAvailabilityApiPayloadForTable(apiRow) : '',
              pinSurCarte: onMap ? 'oui' : 'non',
              visibleDansGrille: hasGridDbg ? (onMap ? 'oui' : 'non') : 'n/a',
            });
          });
          clearTimeout(mapDebugSyncLogTimer);
          mapDebugSyncLogTimer = setTimeout(function () {
            console.log(
              '%c[Loven carte] Pins après sync (debounce 200 ms) — ' +
                nOn +
                ' sur carte, ' +
                nOff +
                ' masqués',
              'font-weight:bold'
            );
            console.table(rowsDbg);
          }, 200);
        } catch (e) {}
      }
    }

    window.syncCollectionMapMarkersToProductGrid = syncMapMarkersToProductGrid;

    offsetColocatedMapLocations(jsonlocations.locations);

    for (var count = 0; count < jsonlocations.locations.length; count++) {
      var loc = jsonlocations.locations[count];

      var marker = createMapMarkerFromLoc(loc, false);
      wireMapMarker(marker, loc);
      var pid = String(loc.id);
      markersByProductId[pid] = marker;
    }

    if (isLovenMapConsoleDebug()) {
      try {
        console.log(
          '%c[Loven carte] Rooms dans le JSON (#collection-map-locations-json)',
          'font-weight:bold'
        );
        var tableJson = jsonlocations.locations.map(function (locRow) {
          var embedU = locRow.embedUrl || '';
          var apiRow = getAvailabilityApiRowForEmbed(embedU);
          return {
            productId: locRow.id,
            title: locRow.title,
            lat: locRow.lat,
            lon: locRow.lon,
            calendrier: locRow.embedUrl ? shortBookingmoodCalendarLabel(locRow.embedUrl) : '',
            dispoApi: getDispoApiLabel(embedU),
            detailApi: apiRow ? formatAvailabilityApiPayloadForTable(apiRow) : '',
          };
        });
        console.table(tableJson);
        console.log('Total (cette page, avec coordonnées Accentuate) :', jsonlocations.locations.length);
      } catch (e) {}
    }

    lovenMapRuntime.map = map;
    lovenMapRuntime.markerCluster = markerCluster;
    lovenMapRuntime.defaultCenter = defaultCenter;
    lovenMapRuntime.defaultZoom = defaultZoom;
    lovenMapRuntime.userControlledViewport = false;
    lovenMapRuntime.mapViewportFilterByUser = false;

    wireMapViewportGridSync(map);
    if (typeof window.LovenSeedZoneCollectionActiveGeo === 'function') {
      window.LovenSeedZoneCollectionActiveGeo();
    }
    syncMapMarkersToProductGrid();
    if (isCollectionZoneMapPage() || isCollectionMapMobileLayout() || isNamedCollectionCatalogPage()) {
      clearMapViewportGridFilter();
    } else {
      runMapViewportGridPipeline(map, { silent: true });
    }
    if (isNamedCollectionCatalogPage()) {
      finalizeNamedCollectionCatalogGrid();
      setTimeout(finalizeNamedCollectionCatalogGrid, 450);
    }
    schedulePostLayoutSync();
    try {
      document.dispatchEvent(new CustomEvent('loven:collection-map-ready'));
    } catch (e) {}
  };

  document.addEventListener('collection:mapSync', function () {
    if (window.syncCollectionMapMarkersToProductGrid) {
      window.syncCollectionMapMarkersToProductGrid(
        lovenMapRuntime.userControlledViewport ? { skipCamera: true } : {}
      );
    }
  });

  document.addEventListener('collection:destFiltered', function () {
    lovenMapRuntime.userControlledViewport = false;
    lovenMapRuntime.mapViewportFilterByUser = false;
    if (isCollectionZoneMapPage() || isCollectionMapMobileLayout() || isNamedCollectionCatalogPage()) {
      clearMapViewportGridFilter();
    }
    var map = lovenMapRuntime.map || window.__lovenCollectionLeafletMap;
    if (map && !isCollectionMapMobileLayout() && !isNamedCollectionCatalogPage()) {
      runMapViewportGridPipeline(map, { silent: true });
    }
  });

  window.addEventListener('loven:sticky-offsets', function () {
    var map = window.__lovenCollectionLeafletMap;
    if (map && map.invalidateSize) {
      requestAnimationFrame(function () {
        map.invalidateSize();
      });
    }
  });

  function observeMapWhenVisible() {
    disconnectMapObserver();
    destroyExistingMap();

    if (!document.getElementById('map') || !document.getElementById('collection-map-locations-json')) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      window.LovenCollectionMapInit();
      return;
    }

    var root = document.getElementById('collection-map-root');
    if (!root) return;

    mapLazyObserver = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            disconnectMapObserver();
            window.LovenCollectionMapInit();
            return;
          }
        }
      },
      {
        root: null,
        rootMargin: '180px 0px',
        threshold: 0,
      }
    );
    mapLazyObserver.observe(root);
  }

  function boot() {
    /* Carte en position fixed + bandeau sticky : IntersectionObserver retarde ou rate l’init ; mobile = init directe. */
    var ajaxRoot = document.getElementById('CollectionAjaxResult');
    var isMobileMap =
      ajaxRoot &&
      ajaxRoot.classList.contains('loven-collection-map-mobile') &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 768px)').matches;

    if (isMobileMap) {
      function tryMobileInit() {
        if (typeof L === 'undefined') {
          setTimeout(tryMobileInit, 40);
          return;
        }
        disconnectMapObserver();
        destroyExistingMap();
        window.LovenCollectionMapInit();
      }
      tryMobileInit();
      return;
    }

    observeMapWhenVisible();
  }

  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('collection:reloaded', function () {
    lovenMapRuntime.userControlledViewport = false;
    lovenMapRuntime.mapViewportFilterByUser = false;
    window.__lovenMapViewportFilterActive = false;
    window.__lovenMapViewportVisible = undefined;
    boot();
  });

  document.addEventListener('loven:dynamic-grid-rendered', function () {
    var map = lovenMapRuntime.map || window.__lovenCollectionLeafletMap;
    if (map) runMapViewportGridPipeline(map, { silent: true, skipFetch: true });
  });

  document.addEventListener('collection:availability', function () {
    if (isNamedCollectionCatalogPage()) {
      if (typeof window.LovenClearMapViewportGridFilter === 'function') {
        window.LovenClearMapViewportGridFilter();
      }
      return;
    }
    var map = lovenMapRuntime.map || window.__lovenCollectionLeafletMap;
    if (!map) return;
    runMapViewportGridPipeline(map, { silent: true, skipFetch: true });
  });

  window.LovenFilterProductGridByMapViewport = function () {
    var map = lovenMapRuntime.map || window.__lovenCollectionLeafletMap;
    if (map) runMapViewportGridPipeline(map);
  };

  /**
   * Remplace le JSON carte (#collection-map-locations-json) et réinitialise Leaflet.
   * Utilisé par le mode résultats API (grille dynamique).
   */
  function buildMarkerFromLocData(loc) {
    if (!loc || typeof L === 'undefined') return null;
    var latN = Number(loc.lat);
    var lonN = Number(loc.lon);
    if (!isFinite(latN) || !isFinite(lonN)) return null;
    var marker = createMapMarkerFromLoc(loc, false);
    wireMapMarker(marker, loc);
    return marker;
  }

  window.LovenCollectionMapReplaceMarkersFromJson = function (geoJson) {
    var el = document.getElementById('collection-map-locations-json');
    if (!el || !geoJson || !Array.isArray(geoJson.locations)) return 0;
    el.textContent = JSON.stringify(geoJson);
    var rt = lovenMapRuntime;
    if (!rt.map || typeof L === 'undefined') return 0;

    if (!rt.markersByProductId) rt.markersByProductId = {};
    Object.keys(rt.markersByProductId).forEach(function (pid) {
      var marker = rt.markersByProductId[pid];
      if (!marker) return;
      removeMarkerFromMapLayers(marker);
      delete rt.markersByProductId[pid];
    });
    offsetColocatedMapLocations(geoJson.locations);

    var n = 0;
    for (var i = 0; i < geoJson.locations.length; i++) {
      var loc = geoJson.locations[i];
      var m = buildMarkerFromLocData(loc);
      if (!m) continue;
      rt.markersByProductId[String(loc.id)] = m;
      n++;
    }
    if (window.syncCollectionMapMarkersToProductGrid) window.syncCollectionMapMarkersToProductGrid();
    schedulePostLayoutSync();
    repaintSponsoredMarkerIcons();
    return n;
  };

  window.LovenCollectionMapApplyLocationsJson = function (geoJson) {
    var el = document.getElementById('collection-map-locations-json');
    if (!el) return;
    if (!geoJson || !Array.isArray(geoJson.locations)) {
      geoJson = { locations: [] };
    }
    if (geoJson.locations.length === 0) {
      if (window.__lovenCollectionMapLiquidBackup && window.__lovenCollectionMapLiquidBackup.locations.length) {
        geoJson = window.__lovenCollectionMapLiquidBackup;
      } else {
        return;
      }
    }
    if (window.__lovenCollectionLeafletMap && typeof window.LovenCollectionMapReplaceMarkersFromJson === 'function') {
      window.LovenCollectionMapReplaceMarkersFromJson(geoJson);
      return;
    }
    el.textContent = JSON.stringify(geoJson);
    disconnectMapObserver();
    if (typeof window.LovenCollectionMapInit === 'function') {
      window.LovenCollectionMapInit();
    }
  };

  window.LovenRunMapViewportGridPipeline = runMapViewportGridPipeline;
  window.LovenClearMapViewportGridFilter = clearMapViewportGridFilter;
  window.LovenInvalidateCollectionMapLayout = invalidateMapLayoutSize;
})();
